import { useState, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import jalaali from 'jalaali-js'
import { getJalaliMonthName, getJalaliWeekdayName } from '@/lib/calendar'

interface JalaliDatePickerProps {
  value: string // YYYY-MM-DD in Gregorian
  onChange: (value: string) => void // Returns YYYY-MM-DD in Gregorian
  disabled?: boolean
}

export function JalaliDatePicker({ value, onChange, disabled }: JalaliDatePickerProps) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [tempJalaliDate, setTempJalaliDate] = useState({ year: 0, month: 0, day: 0 })
  const pickerRef = useRef<HTMLDivElement>(null)

  // Convert Gregorian string to Jalali for display
  const jalaliDate = useMemo(() => {
    if (!value) {
      const now = new Date()
      const { jy, jm, jd } = jalaali.toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate())
      return { year: jy, month: jm, day: jd }
    }
    const [year, month, day] = value.split('-').map(Number)
    const { jy, jm, jd } = jalaali.toJalaali(year, month, day)
    return { year: jy, month: jm, day: jd }
  }, [value])

  // Initialize temp date when picker opens
  const handleOpen = () => {
    setTempJalaliDate(jalaliDate)
    setIsOpen(true)
  }

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'year' | 'month' | 'day') => {
    const newValue = e.target.value
    const newJalali = { ...tempJalaliDate, [type]: parseInt(newValue) || 0 }

    // Validate
    if (newJalali.year < 1300 || newJalali.year > 1500) return
    if (newJalali.month < 1 || newJalali.month > 12) return
    if (newJalali.day < 1 || newJalali.day > 31) return

    // Update temp state only
    setTempJalaliDate(newJalali)
  }

  const confirmDate = () => {
    // Convert to Gregorian and call onChange only on confirmation
    try {
      const { gy, gm, gd } = jalaali.toGregorian(tempJalaliDate.year, tempJalaliDate.month, tempJalaliDate.day)
      const gregorianDate = `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`
      onChange(gregorianDate)
      setIsOpen(false)
    } catch (e) {
      console.error('Invalid date:', e)
    }
  }

  const getDaysInMonth = (year: number, month: number): number => {
    if (month <= 6) return 31
    if (month <= 11) return 30
    // Esfand
    if ((year - 1595) % 2820 * 682 % 2816 < 683) return 30
    return 29
  }

  const handleDayClick = (day: number) => {
    try {
      const { gy, gm, gd } = jalaali.toGregorian(tempJalaliDate.year, tempJalaliDate.month, day)
      const gregorianDate = `${gy}-${String(gm).padStart(2, '0')}-${String(gd).padStart(2, '0')}`
      onChange(gregorianDate)
      setIsOpen(false)
    } catch (e) {
      console.error('Invalid date:', e)
    }
  }

  return (
    <div className="relative" ref={pickerRef}>
      <button
        onClick={handleOpen}
        disabled={disabled}
        className="w-full px-3 py-1.5 text-sm bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
      >
        {(() => {
          // تبدیل تاریخ شمسی به میلادی برای دستیابی به روز هفته
          const { gy, gm, gd } = jalaali.toGregorian(jalaliDate.year, jalaliDate.month, jalaliDate.day)
          const gregorianDate = new Date(gy, gm - 1, gd)
          const weekdayIndex = gregorianDate.getDay()
          console.log('🔍 JalaliDatePicker - Jalali:', jalaliDate, 'Gregorian:', gy, gm, gd, 'Date:', gregorianDate, 'WeekdayIndex:', weekdayIndex)
          const weekdayName = getJalaliWeekdayName(gregorianDate)
          console.log('📛 Weekday Name:', weekdayName)
          return `${weekdayName} ${String(jalaliDate.day).padStart(2, '0')}-${String(jalaliDate.month).padStart(2, '0')}-${jalaliDate.year}`
        })()}
      </button>

      {isOpen && (
        <div className="absolute top-full mt-1 left-0 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-lg p-4 w-80">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={() => {
                const newMonth = tempJalaliDate.month === 1 ? 12 : tempJalaliDate.month - 1
                const newYear = tempJalaliDate.month === 1 ? tempJalaliDate.year - 1 : tempJalaliDate.year
                const newDay = Math.min(tempJalaliDate.day, getDaysInMonth(newYear, newMonth))
                setTempJalaliDate({ year: newYear, month: newMonth, day: newDay })
              }}
              className="px-2 py-1 text-white hover:bg-slate-700 rounded"
            >
              ▶️
            </button>
            <div className="text-center text-white font-semibold">
              {getJalaliMonthName(tempJalaliDate.month)} {tempJalaliDate.year}
            </div>
            <button
              type="button"
              onClick={() => {
                const newMonth = tempJalaliDate.month === 12 ? 1 : tempJalaliDate.month + 1
                const newYear = tempJalaliDate.month === 12 ? tempJalaliDate.year + 1 : tempJalaliDate.year
                const newDay = Math.min(tempJalaliDate.day, getDaysInMonth(newYear, newMonth))
                setTempJalaliDate({ year: newYear, month: newMonth, day: newDay })
              }}
              className="px-2 py-1 text-white hover:bg-slate-700 rounded"
            >
              ◀️
            </button>
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((day) => (
              <div key={day} className="text-center text-xs text-gray-400 font-semibold">
                {day}
              </div>
            ))}
            {(() => {
              // محاسبه روز اول ماه
              const { gy, gm, gd } = jalaali.toGregorian(tempJalaliDate.year, tempJalaliDate.month, 1)
              const firstDate = new Date(gy, gm - 1, gd)
              const firstDayWeekday = firstDate.getDay() // 0-6 میلادی
              
              // تبدیل به شاخص در grid شمسی (0-6 برای شنبه تا جمعه)
              const jalaliWeekdayIndex = (firstDayWeekday + 1) % 7
              
              const paddingCells = Array.from({ length: jalaliWeekdayIndex }, (_, i) => (
                <div key={`padding-${i}`} className="aspect-square" />
              ))
              
              const daysInMonth = getDaysInMonth(tempJalaliDate.year, tempJalaliDate.month)
              const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)
              
              const dayCells = days.map((day) => (
                <button
                  key={day}
                  type="button"
                  onClick={() => handleDayClick(day)}
                  className={`aspect-square text-xs rounded ${
                    day === tempJalaliDate.day
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                  }`}
                >
                  {day}
                </button>
              ))
              
              return [...paddingCells, ...dayCells]
            })()}
          </div>

          {/* Input fields */}
          <div className="flex gap-2 mb-3">
            <div className="flex-1">
              <label className="text-xs text-gray-400">{t('jalaliDatePicker.year')}</label>
              <input
                type="number"
                value={tempJalaliDate.year}
                onChange={(e) => handleInputChange(e, 'year')}
                className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-400">{t('jalaliDatePicker.month')}</label>
              <input
                type="number"
                value={tempJalaliDate.month}
                onChange={(e) => handleInputChange(e, 'month')}
                min="1"
                max="12"
                className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-400">{t('jalaliDatePicker.day')}</label>
              <input
                type="number"
                value={tempJalaliDate.day}
                onChange={(e) => handleInputChange(e, 'day')}
                min="1"
                max="31"
                className="w-full px-2 py-1 text-sm bg-slate-700 border border-slate-600 rounded text-white"
              />
            </div>
          </div>

          {/* Confirm and Cancel Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={confirmDate}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-semibold"
            >
              {t('jalaliDatePicker.confirm')}
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-3 py-2 bg-slate-700 text-white rounded hover:bg-slate-600 text-sm font-semibold"
            >
              {t('jalaliDatePicker.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
